import type { Blocker, BlockerVerdict, Blockers, BrandAlertsPlayer } from './types';

/**
 * Below this the sideguide has nowhere to render: it postpones a pending
 * display and dismisses a shown one. Both rules read this one value.
 */
export const MIN_SCREEN_HEIGHT = 390;

/**
 * Display suppression are split in two: reasons that bail and consume the
 * display ("don't show": video ISI, comments, product tour, iOS switch), and
 * reasons that retry until they clear ("fullscreen / short window / subscription
 * prompt"). `evaluate()` therefore checks every `block` before any `postpone`.
 */
export const composeBlockers = (blockers: Blocker[]): Blockers => ({
    evaluate(): BlockerVerdict {
        for (const blocker of blockers) {
            if (blocker.kind === 'block' && blocker.blocked()) {
                return { kind: 'block', reason: blocker.reason?.() ?? null };
            }
        }
        for (const blocker of blockers) {
            if (blocker.kind === 'postpone' && blocker.blocked()) {
                return { kind: 'postpone' };
            }
        }
        return { kind: 'clear' };
    },
    destroy(): void {
        blockers.forEach((blocker) => blocker.destroy?.());
    },
});

/**
 * Blocked by an external signal — the page calls `setBlocked()`. Today that is
 * the mobile comments sheet; the reason is supplied by the caller because it has
 * to match a `BrandAlertEvent.Events` choice.
 */
export class ManualBlocker implements Blocker {
    readonly kind = 'block' as const;
    private _blocked = false;
    private _reason: string | null = null;

    set(blocked: boolean, reason: string): void {
        this._blocked = blocked;
        this._reason = reason;
    }

    blocked(): boolean {
        return this._blocked;
    }

    reason(): string | null {
        return this._reason;
    }
}

/**
 * Blocked while a flag set by a native (iOS) bridge event is true.
 */
export class NativeFlagBlocker implements Blocker {
    private _blocked = false;
    private readonly _teardown: (() => void)[] = [];

    constructor(
        readonly kind: 'block' | 'postpone',
        events: { on: string; off?: string },
        private readonly _reason: string | null = null,
    ) {
        this._listen(events.on, true);
        if (events.off) this._listen(events.off, false);
    }

    blocked(): boolean {
        return this._blocked;
    }

    reason(): string | null {
        return this._reason;
    }

    destroy(): void {
        this._teardown.forEach((off) => off());
    }

    private _listen(event: string, value: boolean): void {
        const handler = () => {
            this._blocked = value;
        };
        window.addEventListener(event, handler);
        this._teardown.push(() => window.removeEventListener(event, handler));
    }
}

/**
 * Retry while another surface's sideguide is on screen.
 *
 * The backend deliberately does not prevent simultaneous display, and the two
 * surfaces own independent shells, so nothing else stops both appearing at once.
 * Postpone rather than block: the other alert will close, and this one should
 * still get its turn.
 */
export class OtherSurfaceVisibleBlocker implements Blocker {
    readonly kind = 'postpone' as const;
    private _visible = false;
    private readonly _teardown: (() => void)[] = [];

    constructor(private readonly _ownSurface: string) {
        this._listen('brand-alert:shown', true);
        this._listen('brand-alert:dismissed', false);
    }

    blocked(): boolean {
        return this._visible;
    }

    destroy(): void {
        this._teardown.forEach((off) => off());
    }

    private _listen(event: string, visible: boolean): void {
        const handler = (e: Event) => {
            if ((e as CustomEvent).detail?.surface === this._ownSurface) return;
            this._visible = visible;
        };
        document.addEventListener(event, handler);
        this._teardown.push(() => document.removeEventListener(event, handler));
    }
}

/**
 * Retry while the player is fullscreen.
 */
export class PlayerFullscreenBlocker implements Blocker {
    readonly kind = 'postpone' as const;

    constructor(private readonly _player: BrandAlertsPlayer) {}

    blocked(): boolean {
        return this._player.isFullscreen();
    }
}

/**
 * Retry while the viewport is too short to fit the sideguide.
 */
export class ScreenHeightBlocker implements Blocker {
    readonly kind = 'postpone' as const;

    constructor(private readonly _minHeight: number) {}

    blocked(): boolean {
        return window.innerHeight < this._minHeight;
    }
}

/**
 * Retry while an element matching `selector` is on screen.
 *
 * For anything that covers the sideguide and goes away on its own — the
 * subscription interstitial, the Video Assistant's full-screen mobile panel.
 * Queried at evaluation time rather than tracked from an event, so a client
 * built while the element is already up still sees it; `evaluate()` runs again
 * on every postpone tick, so the display resumes once it clears.
 */
export class VisibleElementBlocker implements Blocker {
    readonly kind = 'postpone' as const;

    constructor(private readonly _selector: string) {}

    blocked(): boolean {
        return Boolean(document.querySelector(this._selector));
    }
}
